import { lazy, type ComponentType } from "react";

/**
 * lazy(), but it survives a deploy landing under an open tab.
 *
 * Route chunks are content-hashed, so every publish renames them. A page that
 * was loaded before the publish is still holding the OLD names, and the moment
 * you navigate to a route it has not fetched yet, that request 404s:
 *
 *   TypeError: Failed to fetch dynamically imported module:
 *   https://www.leafnode.co.uk/assets/AuthorMatrix-Bm7kS2G7.js
 *
 * The ErrorBoundary then shows "SIGNAL LOST", which reads like the site is
 * down. It is not — the visitor is simply running a build that no longer
 * exists, and the fix is to fetch the current index.html. Observed live on
 * /author-matrix, where the chunk returned 200 to a fresh client at the same
 * moment the stale tab was getting a 404.
 *
 * So: on a failed chunk import, reload once. The sessionStorage flag is what
 * stops that becoming a reload loop if the chunk is genuinely missing rather
 * than merely renamed — the second failure is allowed to reach the boundary,
 * because at that point something really is wrong and the error is the honest
 * answer.
 *
 * THE FLAG IS RELEASED BY A SUCCESSFUL LOAD OF THAT SAME CHUNK, AND BY NOTHING
 * ELSE. It used to be cleared for every key on every route change, from
 * ScrollToTop in App.tsx. That looked like housekeeping and was in fact an
 * infinite reload loop: useEffect runs on MOUNT as well as on navigation, so
 * the reload this file triggers would remount the app, wipe the flag it had
 * just written, fail the import again, and reload again — for ever. The guard
 * could never fire in the one situation it exists for, because the reload
 * destroyed its own evidence. Releasing per-key on success keeps the original
 * intent (a later deploy gets its own retry rather than being refused by a
 * stale flag) with no such race.
 */
export function lazyRoute<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  key: string,
) {
  const flag = `leafnode:chunk-reload:${key}`;

  return lazy(async () => {
    try {
      const mod = await factory();
      // This chunk demonstrably loads on this build, so it has earned its retry
      // back for whatever the next deploy renames.
      try {
        sessionStorage.removeItem(flag);
      } catch {
        /* private browsing — nothing was stored anyway */
      }
      return mod;
    } catch (err) {
      let alreadyTried = false;
      try {
        alreadyTried = sessionStorage.getItem(flag) === "1";
        if (!alreadyTried) sessionStorage.setItem(flag, "1");
      } catch {
        // Private browsing denies sessionStorage. Without the flag a reload
        // loop is possible, so do not reload at all — show the error instead.
        throw err;
      }

      if (alreadyTried) throw err;

      window.location.reload();
      // Never resolves: the reload is already in flight and resolving here
      // would flash a half-mounted route on the way out.
      return new Promise<never>(() => {});
    }
  });
}
